<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        :block="$vuetify.breakpoint.smAndDown"
        color="hrissecondary" 
        class="px-10 white--text"
        v-on="on"
      >
        Apply Leave
      </v-btn>
    </template>
    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="hrissecondary"
      title="Leave"
      text="Apply Leave"
      class="mb-0"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row v-show="confirmationDialog">
            <v-col>
              <employee-leaves-applications-dialog-conflict-confirmation
                ref="dialogConflictConfirmation"
                @created="closeDialog(), $emit('created')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="leaveType"
                v-validate="{
                  required:true,
                  isValidLeaveType:leaveType
                }"
                :error-messages="errors.collect('leave_type_id')"
                :items="leaveTypes"
                data-vv-name="leave_type_id"
                data-vv-as="leave type"
                item-text="name"
                item-disabled="disable"
                return-object
                label="Leave Type"
                outlined
                @input="leaveTypeChanged"
              />     
            </v-col>
          </v-row>

          <v-row
            v-show="leaveType.requires_document"
            no-gutters
          >
            <v-col cols="12">
              <helper-file-input
                v-model="file" 
                v-validate="fileRules"
                :max-size-in-mb="5"
                :max-number="5"
                :multiple="true"
                data-vv-name="file"
                field-name="file"
                accept-type="image/jpg,image/jpeg,image/png,application/pdf"
                extension="ext:jpg,jpeg,png,pdf"
              >
                <div 
                  slot="activator"
                  class="mb-2"
                >   
                  <v-row>
                    <v-col
                      :cols="`${file ? 2 : 6}`"
                    >
                      <v-btn                    
                        :disabled="progressing"
                        class="mx-2"    
                      >
                        Add file
                      </v-btn>
                      <span> 
                        {{ file ? '' : 'No file chosen' }}
                      </span>
                    </v-col>
                    <v-col
                      v-if="file"
                      cols="10"
                      class="px-2"
                    >
                      <span
                        v-for="(selectedFile, i) in file.selectedFiles"
                        :key="selectedFile.name"
                        class="font-weight-bold"
                      > {{ selectedFile.name }} {{ (i + 1) === file.selectedFiles.length ? '' : ',' }} </span>
                    </v-col>
                  </v-row>
                </div>
              </helper-file-input>
              <span class="red--text text--lighten-1 caption">{{ errors.first('file') }}</span>
            </v-col>
          </v-row>

          <v-row v-if="!_.isEmpty(leaveType)">
            <v-col
              v-if="showDateSelection"
              cols="12"
              md="6"
            >
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDate"
                    v-validate="'required'"
                    label="Start Date"
                    :error-messages="errors.collect('start_date')"
                    data-vv-name="start_date"  
                    data-vv-as="start date"      
                    readonly
                    outlined
                    class="label-position"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="earliestDate"
                  :max="maxStartDate"
                  @input="startDateChanged"
                />
              </v-menu>
            </v-col>
            <v-col
              v-if="showDateSelection"
              cols="12"
              md="6"
            >
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDate"
                    v-validate="'required'"
                    :disabled="disableEndDate"
                    label="End Date"
                    :error-messages="errors.collect('end_date')"
                    data-vv-name="end_date"  
                    data-vv-as="end date"
                    class="label-position"
                    readonly
                    outlined
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"                  
                  :min="startDate ? startDate : earliestDate"
                  :max="latestDate"
                  @input="endDateChanged"
                />
              </v-menu>
            </v-col>
          </v-row>
          
          <v-row
            v-if="leaveType.id!==10"
            no-gutters
          >
            <v-col cols="12">
              <i style="font-size:12px">{{ leaveHint }}</i>
            </v-col>
          </v-row>

          <v-card
            v-if="datesSelected && !leaveType.is_continuous && leaveType.id!==19"
            class="elevation-1"
            max-height="280px"
            data-simplebar
          >            
            <v-container class="pa-0">
              <v-progress-linear
                v-if="leaveDays.length < 1"
                indeterminate
                color="hrissecondary"
              />
              <div v-if="leaveDays.length < 1 && noLeaveDaysMessage">
                <i style="color:red;font-size:14px">{{ noLeaveDaysMessage }}</i>
              </div>
              <v-row
                v-for="(item, i) in leaveDays"
                v-else
                :key="i"
                justify="center"
                align="center"
                no-gutters
              >
                <v-col              
                  cols="12"
                  md="4"
                >
                  <span class="font-weight-medium">
                    {{ item.date }} ({{ $moment(item.date).format('ddd') }}) :
                  </span>
                </v-col>
                <v-col              
                  cols="12"
                  md="7"
                >
                  <v-radio-group
                    v-model="leaveDays[i].leave_session_id"
                    row
                    :disabled="leaveDays[i].disabled"
                  >
                    <v-radio
                      v-for="session in leaveSessions"
                      :key="session.id"
                      :label="session.name"
                      :value="session.id"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-container>
          </v-card>

          <v-card
            v-if="datesSelected && leaveType.id==19"
            class="elevation-1"
            max-height="280px"
            data-simplebar
          >  
            <v-container class="pa-0">
              <v-progress-linear
                v-if="leaveDays.length < 1"
                indeterminate
                color="hrissecondary"
              />
              <div v-if="leaveDays.length < 1 && noLeaveDaysMessage">
                <i style="color:red;font-size:14px">{{ noLeaveDaysMessage }}</i>
              </div>
            </v-container>
          </v-card> 
          
          <div v-if="leaveType.id==19 && !showDateSelection && isValid">
            <i style="color:red;font-size:14px">Your birthday has passed the valid application date</i>
          </div>             

          <div v-if="leaveType.id==19 && !showDateSelection && !isValid">
            <i style="color:red;font-size:14px">Your birthday leave only valid upon confirmation</i>
          </div> 

          <v-row v-if="showReason || backDatedLeave">
            <v-col
              cols="12"
              class="pt-6"
            >
              <v-textarea
                v-model="reason"
                v-validate="'required|max:5000'"
                :rows="2"
                :error-messages="errors.collect('reason')"
                data-vv-name="reason"        
                label="Reason"
                outlined
              />
            </v-col>
          </v-row>
        </v-container>
      </form>
      <v-card-actions>
        <div>
          <p
            v-if="datesSelected"
            class="font-weight-bold"
          >
            Leave Requested: {{ leaveDaysRequested }} Day(s)
          </p>
          <p class="red--text text--lighten-1 caption">
            {{ errorMessage }}
          </p>
        </div>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="closeDialog"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="loading || (leaveType.id==19 && !showDateSelection) || leaveDays.length < 1"
          color="hrissecondary"
          class="px-10 white--text"        
          @click="submitForm"
        >
          Apply
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { mapState } from 'vuex'

  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
      EmployeeLeavesApplicationsDialogConflictConfirmation: () => import('@/components/employee/leaves/applications/DialogConflictConfirmation'),
    },
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        confirmationDialog: false,
        dialog: null,
        file: null,
        loading: false,
        progressing: false,
        isValid: true,
        leaveSessions: [],
        workDays: [],
        holidayDetails: [],
        leaveType: {},
        reason: null,
        startDateMenu: null,
        startDate: null,
        endDateMenu: null,
        endDate: null,
        employeeData: null,
        leaveEntitlements: [],
        leaveDays: [],
        leaveSession: {
          '2': 1,
          '3': 2,
          '4': 3,
          '1': 4,
        },
        workSession: {
          '2': 2,
          '3': 3,
          '4': 4,
          '1': 4,
        },
        leaveLockSettings: [],
      }
    },

    computed: {      
      ...mapState({ employee: 'currentEmployee' }),
      datesSelected () {
        return this.startDate && this.endDate && this.noOfSelectedDays > 0
      },      
      errorMessage () {
        if (!this.selectedEntitlement) return
        let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
        if (this.selectedEntitlement.available_days - carriedOverDays < this.leaveDaysRequested) return '*Not enough available leaves'
            
        return ''
      },  
      leaveDaysRequested () {
        return this.leaveDays.reduce((accumulator, leaveDay) => {
          const day = leaveDay.leave_session_id === 4 ? 1 : 0.5
          
          return accumulator + day
        }, 0)
      },  
      noOfSelectedDays () {
        const startDate = this.$moment(this.startDate)
        const endDate = this.$moment(this.endDate)

        return endDate.diff(startDate, 'days') + 1 // includes day 1
      },     
      selectedEntitlement () {
        if (!this.startDate) return
        return this.leaveEntitlements.find(entitlement => entitlement.leave_type_id === this.leaveType.id && entitlement.year === parseInt(this.startDate.substring(0, 4))) || {}
      },
      carriedOverEntitlementDays () {
        if (!this.startDate) return

        return this.leaveEntitlements.find(entitlement => entitlement.leave_type_id === this.leaveType.id && entitlement.year === (parseInt(this.startDate.substring(0, 4)) + 1))
      },
      leaveTypes () {
        let leaveTypes = []
        this.leaveEntitlements.map(entitlement => {
          if (this.identifyAvailableLeaveType(entitlement.leave_type.id) <= 0) {
            if (entitlement.available_days > 0) {
              entitlement.leave_type.disable = false
            } else {
              entitlement.leave_type.disable = true
            }
            leaveTypes.push(entitlement.leave_type)
          }
        })
        return _.orderBy(_.uniqBy(leaveTypes, 'id'), ['name'], ['asc'])
      },
      workingDays () {
        return this.workDays.filter(workDay => workDay.work_session.start_time !== workDay.work_session.end_time)
      },
      // uncomment if use max selectable date is next working day
      /* nonWorkingDays () {
        return this.workDays.filter(workDay => workDay.work_session.start_time === workDay.work_session.end_time)
      }, */
      holidayDates () {
        return this.holidayDetails.filter(holidayDetail => {
          if (holidayDetail.holiday.is_national) return true

          if (holidayDetail.holiday_states.length > 0) {
            return holidayDetail.holiday_states.some(state => state.state_id === this.employee.city.state_id && state.department_id === this.employee.department_id) 
          }
          return false
        })
          .map(holidayDetail => holidayDetail.date)
      },
      isLeapYear () {
        let y = this.$moment().format('YYYY')
        return !((y % 4 && y % 100) || !(y % 400))
      },      
      showDateSelection () {
        let currentYear = this.$moment().format('YYYY')
        let monthDate = this.$moment(this.employeeData.person.dob).format('MM-DD')
        let bday = currentYear + '-' + monthDate  

        if (this.leaveType.id === 19) {
          const bdayEntitlement = this.leaveEntitlements.find(leaveEntitlement => leaveEntitlement.leave_type_id === 19)

          if (this.$moment(bday) >= this.$moment(bdayEntitlement.start_date)) {
            // leap year checking
            if (monthDate === '02-29' && !this.isLeapYear) {
              let setBday = currentYear + '-03-01'
              if (this.$moment(setBday, 'YYYY-MM-DD').diff(this.$moment(), 'days') < -6) return false
              return true
            } else {
              if (this.$moment(bday, 'YYYY-MM-DD').diff(this.$moment(), 'days') < -6) return false
              return true
            }
          } else {
            this.setIsValid()
            return false
          }
        }
        return true
      },
      earliestDate () {  
        if (this.leaveType.id === 19) {
          let currentYear = this.$moment().format('YYYY')
          let monthDate = this.$moment(this.employeeData.person.dob).format('MM-DD')
          let bday = currentYear + '-' + monthDate

          // special handling for leap year (Feb 29)
          if (monthDate === '02-29' && !this.isLeapYear) {
            let setBday = currentYear + '-03-01'
            return this.$moment(setBday).format('YYYY-MM-DD')
          } 
          return this.$moment(bday).format('YYYY-MM-DD')
        }   
        let selectedLeaveEntitlement = this.leaveEntitlements.filter(entitlement => {
          return entitlement.leave_type_id === this.leaveType.id
        })
        if (selectedLeaveEntitlement.length > 0 && selectedLeaveEntitlement[0].start_date) {
          return this.$moment(selectedLeaveEntitlement[0].start_date).format('YYYY-MM-DD')
        }
        return this.$moment().startOf('year').format('YYYY-MM-DD')
      },
      maxStartDate () {
        if (this.leaveType.id === 19) {
          if (this.earliestDate) {
            let currentYear = this.$moment().format('YYYY')
            let lastDayOfYear = this.$moment(currentYear).endOf('year').format('YYYY-MM-DD')
            let daysLeft = this.$moment(lastDayOfYear).diff(this.earliestDate, 'days')
            // avoid to get date on next year
            if (daysLeft < 6) {
              return this.$moment(this.earliestDate).add(daysLeft, 'd').format('YYYY-MM-DD')
            } else {
              return this.$moment(this.earliestDate).add(6, 'd').format('YYYY-MM-DD')
            }

            // uncomment if use max selectable date is next working day
            /* let appliableDate = this.earliestDate
            let addDay = 0

            const nonWorkingDaysNumber = []

            this.nonWorkingDays.forEach(workingDay => {
              if (workingDay.day === 7) {
                nonWorkingDaysNumber.push(0)
              } else {
                nonWorkingDaysNumber.push(workingDay.day)
              }
            })

            while (
              this.holidayDates.includes(this.$moment(appliableDate).format('YYYY-MM-DD')) || 
              nonWorkingDaysNumber.includes(this.$moment(appliableDate).day())
            ) {
              appliableDate = this.$moment(appliableDate).add(1, 'days').format('YYYY-MM-DD')
              addDay++
            }

            const maxDate = this.$moment(this.earliestDate).add(addDay, 'd').format('YYYY-MM-DD')

            if (maxDate < lastDayOfYear) {
              return maxDate
            } else return lastDayOfYear */
          }
        }           
        if (this.leaveType.only_allow_backdated_application) {
          return this.$moment().format('YYYY-MM-DD')
        }
        return this.latestDate
      },
      latestDate () {
        let selectedLeaveEntitlement = this.leaveEntitlements.filter(entitlement => {
          return entitlement.leave_type_id === this.leaveType.id
        })
        if (selectedLeaveEntitlement.length > 0 && selectedLeaveEntitlement[0].start_date) {
          return this.$moment(selectedLeaveEntitlement[0].start_date).endOf('year').format('YYYY-MM-DD')
        }
        return this.$moment().endOf('year').format('YYYY-MM-DD')
      },
      fileRules () {
        return this.leaveType.requires_document ? 'required' : ''
      },
      leaveHint () {
        let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
        return _.isEmpty(this.startDate) ? '' : `*Leave Available: ${this.roundedTwoDecimal(this.selectedEntitlement.available_days - carriedOverDays)} Day(s)` 
      },
      noLeaveDaysMessage () {
        let isSingular = true
        let message = ''
        if (!this.leaveType.includes_public_holiday) {
          message = 'Public Holiday'
        }
        if (!this.leaveType.includes_off_day) {
          if (message !== '') {
            message = message + ', Off Day'
            isSingular = false
          } else {
            message = 'Off Day'
          }
        }
        if (!this.leaveType.includes_rest_day) {
          if (message !== '') {
            message = message + ', Rest Day'
            isSingular = false
          } else {
            message = 'Rest Day'
          }
        }

        if (message === '') {
          return message
        }
        if (isSingular) {
          message = message + ' is not included in ' + this.leaveType.name
        } else {
          message = message + ' are not included in ' + this.leaveType.name
        }
        return message
      },
      disableEndDate () {
        if (this.leaveType.is_continuous || this.leaveType.id === 19) return true
        return false
      },
      showReason () {
        if (!this.leaveType || !this.startDate) return false
        if (this.leaveType.id === 19) {
          if (this.$moment(this.employeeData.person.dob).format('MM-DD') === this.$moment(this.startDate).format('MM-DD')) {
            return false
          } else {
            return true
          }
        }
        return this.leaveType.requires_reason && this.leaveType.minimum_days_reason_required === 0 ? true
          : this.$moment(this.startDate, 'YYYY-MM-DD').diff(this.$moment().format('YYYY-MM-DD'), 'days') + 1 <= this.leaveType.minimum_days_reason_required
      },
      backDatedLeave () {
        if (!this.leaveType || !this.startDate) return false
        
        if (this.$moment(this.startDate).diff(this.$moment(), 'days') + 1 <= 0) {
          return true
        }
        return false
      },
    },
    watch: {
      dialog (value) {
        if (value) {
          let scope = this

          this.loadData()
          this.$validator.resume()

          this.$validator.extend('isValidLeaveType', {
            getMessage (field, args) {
              let leaveType = null
              if (args.id === 3) {
                leaveType = scope.leaveTypes.find(type => type.id === 15) // UL2

                if (!leaveType) {
                  return 'Please request UNPAID LEAVE >14 DAYS from HR'
                }
              } else if (args.id === 15) {
                leaveType = scope.leaveTypes.find(type => type.id === 3) // UL
              }
              
              return 'Please select ' + leaveType.name.toUpperCase()   
            },
            validate (value, args) {
              return scope.isValidateLeaveType(value) 
            },
          })
        } else {
          this.$resetData()
        }
      },
    },
 
    methods: {
      setIsValid () {
        this.isValid = false
      },
      identifyAvailableLeaveType (selectedLeaveTypeId) {
        return this.leaveLockSettings.filter(lock => {
          return lock.leave_type_id === selectedLeaveTypeId && (lock.start_date <= this.$moment().format('YYYY-MM-DD') && lock.end_date >= this.$moment().format('YYYY-MM-DD'))
        }).length
      },
      roundedTwoDecimal (value) {
        return Math.round(value * 100, 0) / 100
      },
      isValidateLeaveType (value) {
        if (value.id === 3 && this.leaveDaysRequested > 14) { // unpaid leave
          return false
        } else if (value.id === 15 && this.leaveDaysRequested <= 14 && this.leaveDaysRequested > 0) { // unpaid leave 2
          return false
        }

        return true
      },
      closeDialog () {
        this.$validator.pause()
        this.dialog = false
      },
      loadData () {
        this.loading = true

        Promise.all([
          this.loadLeaveSessions(),
          this.loadWorkDays(),
          this.loadLeaveApplications(),
          this.$api.hrisEmployees().show(this.employee.employee_uid),
          this.$api.leaveLockSettings().yearFilteredList((new Date()).getFullYear()),
        ]).then(([leaveSession, workDay, leaveApplication, employeeDetail, leaveLockSetting]) => {
          this.leaveLockSettings = leaveLockSetting.data

          this.leaveSessions = leaveSession.data.filter(session => session.name.toUpperCase() !== 'NONE')
          this.workDays = workDay.data
          this.employeeData = employeeDetail.data

          leaveApplication.data.map(item => {
            item.available_days = item.available_days - this.getPendingApplications(item.leave_applications)
          })

          let annualLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 1) // annual leave
          let unpaidLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 3) // unpaid leave
          let sickLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 2) // sick leave
          let unpaidSickLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 17) // unpaid sick leave
          let covidPaidTimeOffLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 10) // covid paid time off leave
          let hospitalizationLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 11) // hospitalization leave
          let birthdayLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 19) // birthday leave
          let compassionateLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 6) // compassionate leave
          let marriageLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 7) // marriage leave
          let naturalDisasterLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 9) // natural disaster leave
          let paternityLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 4) // paternity leave
          let prolongLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 12) // prolong medical disaster leave
          let examinationLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 8) // examination leave         

          this.leaveEntitlements = leaveApplication.data.filter(entitlement => {
            if (annualLeave && annualLeave.leave_type && entitlement.leave_type_id === annualLeave.leave_type.id) {
              if (this.$moment(this.employeeData.confirmed_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD') && annualLeave.available_days >= 0.5) {
                return entitlement
              }
            } else if (unpaidLeave && unpaidLeave.leave_type && entitlement.leave_type_id === unpaidLeave.leave_type.id && (annualLeave && this.$moment(annualLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD'))) {
              if (annualLeave && annualLeave.year === entitlement.year) {
                if (annualLeave.available_days < 0.5) {
                  return entitlement
                }
              } else {
                return entitlement
              }
            } else if (sickLeave && sickLeave.leave_type && entitlement.leave_type_id === sickLeave.leave_type.id) {
              if (sickLeave.available_days >= 0) {
                return entitlement
              }
            } else if (unpaidSickLeave && unpaidSickLeave.leave_type && entitlement.leave_type_id === unpaidSickLeave.leave_type.id) {
              if (sickLeave) {
                return entitlement
              } else {
                if (sickLeave.available_days < 0.5) {
                  return entitlement
                }
              }
            } else if (covidPaidTimeOffLeave && covidPaidTimeOffLeave.leave_type && entitlement.leave_type_id === covidPaidTimeOffLeave.leave_type.id) {
              if (covidPaidTimeOffLeave.entitled_days >= 0) {
                covidPaidTimeOffLeave.available_days = 5 - covidPaidTimeOffLeave.entitled_days_taken
                if (covidPaidTimeOffLeave.entitled_days_taken > 5) {
                  hospitalizationLeave.available_days = hospitalizationLeave.entitled_days - covidPaidTimeOffLeave.entitled_days_taken - hospitalizationLeave.entitled_days_taken
                } else {
                  hospitalizationLeave.available_days = hospitalizationLeave.entitled_days - 5 - hospitalizationLeave.entitled_days_taken
                }
              }

              return entitlement
            } else if (birthdayLeave && birthdayLeave.leave_type && entitlement.leave_type_id === birthdayLeave.leave_type.id) {
              if (this.$moment(birthdayLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }
            } else if (compassionateLeave && compassionateLeave.leave_type && entitlement.leave_type_id === compassionateLeave.leave_type.id) {
              if (this.$moment(compassionateLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }
            } else if (marriageLeave && marriageLeave.leave_type && entitlement.leave_type_id === marriageLeave.leave_type.id) {
              if (this.$moment(marriageLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }
            } else if (naturalDisasterLeave && naturalDisasterLeave.leave_type && entitlement.leave_type_id === naturalDisasterLeave.leave_type.id) {
              if (this.$moment(naturalDisasterLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }        
            } else if (paternityLeave && paternityLeave.leave_type && entitlement.leave_type_id === paternityLeave.leave_type.id) {
              if (this.$moment(paternityLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }   
            } else if (prolongLeave && prolongLeave.leave_type && entitlement.leave_type_id === prolongLeave.leave_type.id) {
              if (this.$moment(prolongLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              } 
            } else if (examinationLeave && examinationLeave.leave_type && entitlement.leave_type_id === examinationLeave.leave_type.id) {
              if (this.$moment(examinationLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }                
            } else {
              return entitlement
            }
          }).filter(entitlement => {
            return entitlement.leave_type.is_apply_on_behalf === 0
          })

          this.loading = false
        })
      },
      startDateChanged () {
        Promise.all([
          this.loadHolidays(),
        ]).then(([holiday]) => {
          this.holidayDetails = holiday.data
          this.clearLeaveDays()
          this.computeLeaveDates()
          this.computeLeaveDays()

          this.$validator.validate('leave_type_id')
        })
      },
      getComputedEndDate (totalDays) {
        let getDate = this.$moment(this.startDate)
        for (var i = 0; i <= totalDays; i++) {
          getDate = i < 0 ? this.$moment(this.startDate) : this.$moment(this.startDate).add(i, 'days').format('YYYY-MM-DD')
          const isTodayHoliday = this.holidayDates.includes(this.$moment(getDate).format('YYYY-MM-DD'))
          if ((!this.leaveType.includes_off_day && this.$moment(getDate).day() === 6) || 
            (!this.leaveType.includes_rest_day && this.$moment(getDate).day() === 0) ||
            (!this.leaveType.includes_public_holiday && isTodayHoliday)) {
            if (this.leaveType.id === 19) {
              let currentYear = this.$moment().format('YYYY')
              let getDateYear = this.$moment(getDate).format('YYYY')

              if (getDateYear > currentYear) {
                return totalDays
              }
            } else {
              totalDays = totalDays + 1
            }
          }
        }
        return this.$moment(getDate).format('YYYY-MM-DD')
      },
      computeLeaveDates () {
        this.startDateMenu = false
        // let totalDays = this.$moment(this.startDate).add(1, 'days').diff(this.$moment(this.startDate), 'days')
        this.endDate = this.startDate
        
        if (this.leaveType.is_continuous) {
          let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
          
          let totalDays = this.selectedEntitlement.available_days - carriedOverDays - 1
          this.endDate = this.getComputedEndDate(totalDays)
        }
      },
      endDateChanged () {
        this.endDateMenu = false

        this.clearLeaveDays()
        this.computeLeaveDays()

        this.$validator.validate('leave_type_id')
      },
      leaveTypeChanged () {
        this.startDate = null
        this.endDate = null
        this.file = null
        this.reason = null

        this.clearLeaveDays()

        // uncomment if use max selectable date is next working day
        /* if (this.leaveType.id === 19) {
          this.$api.hrisHolidayDetails().list('?filter[year]=' + this.earliestDate.substring(0, 4))
            .then(res => {
              this.holidayDetails = res.data
            })
        } */
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            if (this.errorMessage) return          

            this.progressing = true

            this.$api.leaveApplications().create(this.selectedEntitlement.id, this.prepareFormData(false))
              .then(response => {
                this.$emit('created')
                this.$snack({ message: 'Leave Applied' })

                this.closeDialog()
              })
              .catch(error => {
                if (error.response.status === 409) {
                  this.$refs.dialogConflictConfirmation.toggleDialog(this.selectedEntitlement, this.prepareFormData(true))
                  this.progressing = false
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },   
      loadLeaveApplications () {
        return this.$api.leaveApplications().list(this.employee.employee_uid)
      },
      loadLeaveSessions () {
        return this.$api.leaveSessions().list()
      },      
      loadWorkDays () {
        return this.$api.employeeWorkdays().list(this.employee.employee_uid)
      },
      loadHolidays () {
        return this.$api.hrisHolidayDetails().list('?filter[year]=' + this.startDate.substring(0, 4))
      },
      prepareFormData (toReplace) {
        const getReason = this.reason ? this.reason : 'On ' + this.selectedEntitlement.leave_type.name.toLowerCase()

        if (this.file) {
          this.file.formData.set('start_date', this.startDate)
          this.file.formData.set('end_date', this.endDate)
          this.file.formData.set('reason', getReason)
          this.file.formData.set('leave_days', JSON.stringify(this.leaveDays))
          this.file.formData.set('to_replace', toReplace)

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('start_date', this.startDate)
        formData.append('end_date', this.endDate)
        formData.append('reason', getReason)
        formData.append('leave_days', JSON.stringify(this.leaveDays))
        formData.append('to_replace', toReplace)

        return formData
      },
      computeLeaveDays () {
        if (!this.datesSelected) return 
        
        let leaveDates = this.getLeaveDates(this.startDate, this.endDate, this.noOfSelectedDays)

        leaveDates.filter(leaveDate => {
          const isTodayHoliday = this.holidayDates.includes(leaveDate)
          if (this.leaveType.is_continuous || this.leaveType.includes_rest_day || this.leaveType.includes_off_day || this.leaveType.includes_public_holiday) {
            // Holiday
            if (isTodayHoliday && this.leaveType.includes_public_holiday) return leaveDate
            
            // Sunday + Holiday
            let isTodayRestDay = false
            if (this.$moment(leaveDate).day() === 0) isTodayRestDay = true
            if (!isTodayHoliday && isTodayRestDay && this.leaveType.includes_rest_day) return leaveDate 

            // Saturday + Holiday
            let isTodayOffDay = false
            if (this.$moment(leaveDate).day() === 6) isTodayOffDay = true
            if (!isTodayHoliday && isTodayOffDay && this.leaveType.includes_off_day) return leaveDate

            // Work Day
            if (!isTodayRestDay && !isTodayOffDay && !isTodayHoliday) return leaveDate
          } else {
            // leave date filter by work day
            const isWorkDay = this.workingDays.find(workingDay => {
              const isoWorkingDay = workingDay.day === 7 ? 0 : workingDay.day
            
              return isoWorkingDay === this.$moment(leaveDate).day()
            })
            
            if (isWorkDay && !isTodayHoliday) return leaveDate
          }
        }).map((leaveDate, i) => {
          if (this.leaveType.is_continuous || this.leaveType.id === 11) {
            this.leaveDays.push({
              date: leaveDate,
              leave_session_id: 4,
              disabled: true,
            })
          } else {
            this.workDays.map(item => {
              const isoWorkingDay = item.work_session.day === 7 ? 0 : item.work_session.day
            
              if (this.$moment(leaveDate).day() === isoWorkingDay) {
                this.leaveDays.push({
                  date: leaveDate,
                  leave_session_id: this.workSession[item.leave_session.id],
                  disabled: item.leave_session.name.toUpperCase() !== 'NONE',
                })
              }
            })
          }
        })      
      },
      getLeaveDates (selectedStartDate, selectedEndDate, interval) {
        let dateArray = []
        let startDate = this.$moment(selectedStartDate)
        const endDate = this.$moment(selectedEndDate)

        while (startDate <= endDate) {
          dateArray.push(this.$moment(startDate).format('YYYY-MM-DD'))

          startDate = this.$moment(startDate).add(1, 'days')
        }
        
        return dateArray
      },  
      clearLeaveDays () {
        this.leaveDays = []
      },      
      getPendingApplications (leaveApplications) {
        let sum = 0
        leaveApplications.map(application => {
          if (application.approval_request.approvals[application.approval_request.approvals.length - 1].status.id === 100) {
            sum = sum + application.requested_days
          }
        })

        return sum
      },
    },
  }
</script>

<style lang="css" scoped>
.card-overflow {
  height: 40vh;
  overflow-y: scroll;
}
::-webkit-scrollbar {
  width: 0px;  /* Remove scrollbar space */
  background: transparent;  /* Optional: just make scrollbar invisible */
}
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
